import React from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, Image, Film, Users, Box, Palette, Archive, Wand2, Sparkles, LayoutGrid, Clapperboard } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

/**
 * 资源树形导航组件
 */
const ResourceTree = ({ treeData, activeNode, onNodeClick }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [expandedNodes, setExpandedNodes] = React.useState(new Set(['episodes']));

  if (!treeData) return null;

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const isActive = (node) => {
    if (!activeNode) return false;
    return activeNode.type === node.type && activeNode.id === node.id;
  };

  const renderNode = (node, level = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const active = isActive(node);

    return (
      <div key={node.id}>
        <button
          type="button"
          className="studio-resource-node flex w-full items-center gap-2 px-2 py-2 rounded-md cursor-pointer text-left text-xs"
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-current={active ? 'location' : undefined}
          style={{
            paddingLeft: `${level * 12 + 8}px`,
            backgroundColor: active ? 'var(--studio-selected-fill, var(--color-bg-overlay))' : 'transparent',
            color: active ? theme.colors.accentPrimary : 'var(--color-text-primary)'
          }}
          onClick={() => {
            if (hasChildren) toggleNode(node.id);
            onNodeClick(node);
          }}
        >
          {hasChildren && (
            <span className="shrink-0">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          )}
          {!hasChildren && <span className="w-3.5" />}

          <span className="shrink-0" style={{ color: active ? theme.colors.accentPrimary : 'var(--studio-muted, var(--color-text-muted))' }}>
            {node.icon}
          </span>

          <span className="flex-1 truncate font-medium">{node.label}</span>

          {node.count && (
            <span className="shrink-0 text-2xs px-1.5 py-0.5 rounded"
              style={{ backgroundColor: 'transparent', color: 'var(--studio-muted, var(--color-text-muted))' }}>
              {node.count.images || 0}图 {node.count.videos || 0}视
            </span>
          )}
        </button>

        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // 构建树形结构
  const buildTree = () => {
    const tree = [];

    // 1. 集数和分镜
    if (treeData.episodes && treeData.episodes.length > 0) {
      const episodesNode = {
        id: 'episodes',
        type: 'category',
        label: '分镜资源',
        icon: <Folder size={14} />,
        children: treeData.episodes.map(ep => ({
          id: `episode_${ep.episodeId}`,
          type: 'episode',
          episodeId: ep.episodeId,
          label: ep.episodeTitle || `第${ep.sortOrder}集`,
          icon: <FolderOpen size={14} />,
          color: theme.colors.bluePrimary,
          count: { images: ep.totalImages, videos: ep.totalVideos },
          children: ep.shots.map(shot => ({
            id: `shot_${ep.episodeId}_${shot.shotNumber}`,
            type: 'shot',
            episodeId: ep.episodeId,
            shotNumber: shot.shotNumber,
            label: `#${String(shot.shotNumber).padStart(3, '0')}`,
            icon: <Film size={14} />,
            count: { images: shot.imageCount, videos: shot.videoCount }
          }))
        }))
      };
      tree.push(episodesNode);
    }

    // 2. 素材资产
    if (treeData.assets) {
      const assetsNode = {
        id: 'assets',
        type: 'category',
        label: '素材资产',
        icon: <Box size={14} />,
        children: []
      };

      if (treeData.assets.characters > 0) {
        assetsNode.children.push({
          id: 'asset_characters',
          type: 'asset_category',
          category: 'characters',
          label: '角色素材',
          icon: <Users size={14} />,
          color: theme.colors.accentPrimary,
          count: { images: treeData.assets.characters, videos: 0 }
        });
      }

      if (treeData.assets.scenes > 0) {
        assetsNode.children.push({
          id: 'asset_scenes',
          type: 'asset_category',
          category: 'scenes',
          label: '场景素材',
          icon: <Palette size={14} />,
          color: theme.colors.greenPrimary,
          count: { images: treeData.assets.scenes, videos: 0 }
        });
      }

      if (treeData.assets.props > 0) {
        assetsNode.children.push({
          id: 'asset_props',
          type: 'asset_category',
          category: 'props',
          label: '道具素材',
          icon: <Box size={14} />,
          color: theme.colors.orangePrimary,
          count: { images: treeData.assets.props, videos: 0 }
        });
      }

      if (assetsNode.children.length > 0) {
        tree.push(assetsNode);
      }
    }

    // 3. AI生成资源（图片Agent / 视频Agent / 画布模式 / 动态分镜等）
    if (treeData.aiGenerated && Object.keys(treeData.aiGenerated).length > 0) {
      const sourceLabels = {
        image_agent: { label: '图片Agent', icon: <Image size={14} />, color: theme.colors.accentPrimary },
        video_agent: { label: '视频Agent', icon: <Film size={14} />, color: theme.colors.bluePrimary },
        canvas: { label: '画布模式', icon: <LayoutGrid size={14} />, color: theme.colors.purplePrimary },
        canvas_url: { label: '画布上传', icon: <LayoutGrid size={14} />, color: theme.colors.purplePrimary },
        motion_storyboard: { label: '动态分镜', icon: <Clapperboard size={14} />, color: theme.colors.purplePrimary },
      };

      const aiNode = {
        id: 'ai_generated',
        type: 'category',
        label: 'AI 生成',
        icon: <Sparkles size={14} />,
        children: Object.entries(treeData.aiGenerated).map(([sourceDetail, counts]) => {
          const config = sourceLabels[sourceDetail] || { label: sourceDetail, icon: <Wand2 size={14} />, color: 'var(--color-text-muted)' };
          return {
            id: `ai_${sourceDetail}`,
            type: 'ai_source',
            sourceDetail,
            label: config.label,
            icon: config.icon,
            color: config.color,
            count: { images: counts.images || 0, videos: counts.videos || 0 },
          };
        })
      };

      if (aiNode.children.length > 0) {
        tree.push(aiNode);
      }
    }

    // 4. 废案
    if (treeData.archivedCount > 0) {
      tree.push({
        id: 'archived',
        type: 'archived',
        label: '废案',
        icon: <Archive size={14} />,
        color: 'var(--color-text-muted)',
        count: { images: treeData.archivedCount, videos: 0 }
      });
    }

    return tree;
  };

  const treeNodes = buildTree();

  return (
    <div className="h-full overflow-y-auto custom-scrollbar p-2 space-y-1">
      {treeNodes.map(node => renderNode(node))}
    </div>
  );
};

export default ResourceTree;
